// Modal picker — choose one store or open all configured catalog searches.
import React, { useMemo, useState } from 'react';
import { X, ExternalLink, Layers } from 'lucide-react';
import {
  buildPartSearchUrl,
  openPartSearchUrl,
  openAllPartSearchUrls,
  resolvePartSearchLinks,
} from './lib/part-search-links.js';

export default function PartSearchPicker({
  open,
  query,
  vehicleLabel = '',
  partSearchLinks,
  onClose,
}) {
  const [includeVehicle, setIncludeVehicle] = useState(true);

  const links = useMemo(
    () => resolvePartSearchLinks(partSearchLinks),
    [partSearchLinks],
  );

  const trimmedQuery = String(query || '').trim();
  const searchOpts = {
    vehicleLabel,
    includeVehicle: includeVehicle && !!vehicleLabel?.trim(),
  };

  if (!open) return null;

  function openOne(link) {
    const url = buildPartSearchUrl(link.url, trimmedQuery, searchOpts);
    if (!url) return;
    openPartSearchUrl(url);
  }

  function openAll() {
    openAllPartSearchUrls(links, trimmedQuery, searchOpts);
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="modal-box part-search-picker" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>Where should we search?</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {!trimmedQuery ? (
          <p className="confirm-dialog-body">Type a part name or number first, then tap Search.</p>
        ) : (
          <>
            <p className="part-search-picker-query">
              Searching for: <strong>{trimmedQuery}</strong>
            </p>
            {vehicleLabel && (
              <label className="part-lookup-vehicle part-search-picker-vehicle">
                <input
                  type="checkbox"
                  checked={includeVehicle}
                  onChange={(e) => setIncludeVehicle(e.target.checked)}
                />
                <span className="small">Include vehicle ({vehicleLabel})</span>
              </label>
            )}

            {links.length > 1 && (
              <button type="button" className="btn part-search-open-all" onClick={openAll}>
                <Layers size={16} />
                Open all {links.length} stores
              </button>
            )}

            <ul className="part-search-picker-list">
              {links.map((link) => (
                <li key={link.id || link.label}>
                  <button
                    type="button"
                    className="part-search-picker-btn"
                    onClick={() => openOne(link)}
                  >
                    <span>{link.label}</span>
                    <ExternalLink size={16} />
                  </button>
                </li>
              ))}
            </ul>

            <p className="muted small part-search-picker-hint">
              Each store opens in a new tab. Close this when you are done — you stay on the job.
            </p>
          </>
        )}

        <div className="form-actions">
          <button type="button" className="btn-link" onClick={onClose}>
            Back to job
          </button>
        </div>
      </div>
    </div>
  );
}

// First non-empty line — handy when a textarea holds notes + part names.
export function partSearchQueryFromText(text) {
  const lines = String(text || '').split(/\r?\n/);
  const line = lines.map((l) => l.trim()).find(Boolean) || '';
  // Strip trailing " — $15" style price notes from extra-parts field.
  return line.replace(/\s*[—–-]\s*\$?\d.*$/, '').trim() || line;
}
